import { useEffect, useLayoutEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Printer, Info } from "lucide-react";

// Label uses ALL-CAPS wording (never Title Case), matching the SKU convention.
const labelText = (s) => String(s || "").toUpperCase();

// Parse "30mm*20mm" → { length: 30, width: 20 }
const parseStickerSize = (sz) => {
  const m = String(sz || "30mm*20mm").match(/(\d+(?:\.\d+)?)\s*mm\s*[*x×]\s*(\d+(?:\.\d+)?)\s*mm/i);
  if (!m) return { length: 30, width: 20, label: "30mm*20mm" };
  return { length: parseFloat(m[1]), width: parseFloat(m[2]), label: `${m[1]}mm*${m[2]}mm` };
};

const SIZE_OPTIONS = ["30mm*20mm", "40mm*20mm", "70mm*30mm"];

// -----------------------------------------------------------------------------
// The real-mm print label. Rendered at physical size (no scale) — placed OFF-SCREEN
// on screen view (via CSS) so it can still be laid out & measured, but flipped
// to on-page rendering during @media print.
// -----------------------------------------------------------------------------
function LabelPrintable({ top, bottom, dim, onFit }) {
  const topRef = useRef(null);
  const bottomRef = useRef(null);

  // Auto-fit each line independently, measuring the REAL mm element.
  useLayoutEffect(() => {
    const fitLine = (el, maxMm) => {
      if (!el) return;
      let size = maxMm;
      el.style.fontSize = `${size}mm`;
      const parent = el.parentElement;
      if (!parent || parent.clientWidth === 0) return;
      let guard = 400;
      while (size > 0.6 && el.scrollWidth > parent.clientWidth && guard-- > 0) {
        size -= 0.05;
        el.style.fontSize = `${size}mm`;
      }
      return size;
    };
    // Each line owns half the label inner-height (paper - 0.4mm padding total)
    const perLine = (dim.width - 0.4) / 2;
    const run = () => {
      const t = fitLine(topRef.current, perLine);
      const b = fitLine(bottomRef.current, perLine);
      onFit?.({ topMm: t, bottomMm: b });
    };
    run();
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(run).catch(() => {});
    }
  }, [top, bottom, dim.length, dim.width]);

  return (
    <div className="label-print"
      style={{
        width: `${dim.length}mm`, height: `${dim.width}mm`,
        boxSizing: "border-box",
        padding: "0.2mm",
        display: "flex", flexDirection: "column",
        overflow: "hidden",
        background: "white",
        fontFamily: "'Roboto Condensed', 'Arial Narrow Bold', Arial, sans-serif",
        fontWeight: 700, letterSpacing: "0.01em", color: "#000",
      }}>
      <div className="label-line" style={{
        width: "100%", height: "50%",
        display: "flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden",
      }}>
        <span ref={topRef} style={{
          whiteSpace: "nowrap", lineHeight: 1,
          fontFamily: "'Roboto Condensed', 'Arial Narrow Bold', Arial, sans-serif",
          fontWeight: 700,
        }}>{top}</span>
      </div>
      <div className="label-line" style={{
        width: "100%", height: "50%",
        display: "flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden",
      }}>
        <span ref={bottomRef} style={{
          whiteSpace: "nowrap", lineHeight: 1,
          fontFamily: "'Roboto Condensed', 'Arial Narrow Bold', Arial, sans-serif",
          fontWeight: 700,
        }}>{bottom}</span>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Screen-only enlarged preview (scale(3)). Mirrors the printable rendering
// but is CSS-transformed for visibility only.
// -----------------------------------------------------------------------------
function ScreenPreview({ top, bottom, dim, topMm, bottomMm }) {
  return (
    <div style={{
      display: "flex", justifyContent: "center", alignItems: "center",
      padding: "12px",
    }}>
      <div style={{
        width: `${dim.length}mm`, height: `${dim.width}mm`,
        boxSizing: "border-box", padding: "0.2mm",
        border: "1px dashed #94a3b8", background: "white",
        display: "flex", flexDirection: "column",
        fontFamily: "'Roboto Condensed', 'Arial Narrow Bold', Arial, sans-serif",
        fontWeight: 700, letterSpacing: "0.01em",
        transform: "scale(3)", transformOrigin: "center",
        margin: `${dim.width * 1.5}mm 0`,
      }}>
        <div style={{
          width: "100%", height: "50%",
          display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden",
        }}>
          <span style={{ whiteSpace: "nowrap", lineHeight: 1, fontSize: `${topMm || 0}mm` }}>{top}</span>
        </div>
        <div style={{
          width: "100%", height: "50%",
          display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden",
        }}>
          <span style={{ whiteSpace: "nowrap", lineHeight: 1, fontSize: `${bottomMm || 0}mm` }}>{bottom}</span>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Main dialog. `copiesDefault` controls the initial "Copies" value (1 for
// Master SKU, item.quantity for Arrivals).
// -----------------------------------------------------------------------------
export function SkuLabelPrintDialog({ open, onOpenChange, sku, copiesDefault = 1, allowQuantityToggle = false }) {
  const [size, setSize] = useState(sku?.sticker_size || "30mm*20mm");
  const [copies, setCopies] = useState(copiesDefault);
  const [fitMm, setFitMm] = useState({ topMm: 0, bottomMm: 0 });
  useEffect(() => {
    if (sku) {
      setSize(sku.sticker_size || "30mm*20mm");
      setCopies(copiesDefault);
    }
  }, [sku?.id, copiesDefault]);

  const dim = parseStickerSize(size);
  const top = labelText(sku?.sticker_top || sku?.item_name_2 || sku?.sku || "");
  const bottom = labelText(sku?.sticker_bottom || sku?.variant || "");

  const doPrint = () => {
    // Bulletproof approach: create an isolated iframe that contains ONLY the
    // label HTML with its own @page size + fonts, so browser Print Preview
    // uses the exact physical mm size (no scaling, no parent CSS interference).
    const cnt = Math.max(1, Math.min(500, parseInt(copies, 10) || 1));
    const esc = (s) => String(s || "").replace(/[&<>"']/g,
      c => ({"&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;"}[c]));
    const labelHtml = Array.from({ length: cnt }).map(() => `
      <div class="label">
        <div class="line"><span>${esc(top)}</span></div>
        <div class="line"><span>${esc(bottom)}</span></div>
      </div>`).join("");
    const html = `<!doctype html>
      <html><head>
        <meta charset="utf-8">
        <link rel="preconnect" href="https://fonts.googleapis.com">
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
        <link href="https://fonts.googleapis.com/css2?family=Roboto+Condensed:wght@700&display=swap" rel="stylesheet">
        <style>
          @page { size: ${dim.length}mm ${dim.width}mm; margin: 0; }
          html, body { margin: 0; padding: 0; background: #fff; }
          .label {
            width: ${dim.length}mm; height: ${dim.width}mm;
            box-sizing: border-box; padding: 0.2mm;
            display: flex; flex-direction: column;
            overflow: hidden;
            font-family: 'Roboto Condensed', 'Arial Narrow Bold', Arial, sans-serif;
            font-weight: 700; color: #000;
            break-after: page; page-break-after: always;
          }
          .label:last-child { break-after: auto; page-break-after: auto; }
          .line {
            width: 100%; height: 50%;
            display: flex; align-items: center; justify-content: center;
            overflow: hidden;
          }
          .line span { white-space: nowrap; line-height: 1; letter-spacing: 0.01em; }
        </style>
      </head><body>
        ${labelHtml}
        <script>
          (function() {
            var perLine = (${dim.width} - 0.4) / 2;
            function fit() {
              var labels = document.querySelectorAll('.label');
              for (var i = 0; i < labels.length; i++) {
                var spans = labels[i].querySelectorAll('.line span');
                for (var j = 0; j < spans.length; j++) {
                  var s = spans[j];
                  var size = perLine;
                  s.style.fontSize = size + 'mm';
                  var parent = s.parentElement;
                  var guard = 500;
                  while (size > 0.6 && s.scrollWidth > parent.clientWidth && guard-- > 0) {
                    size -= 0.05;
                    s.style.fontSize = size + 'mm';
                  }
                }
              }
            }
            function go() {
              fit();
              window.focus();
              window.print();
              setTimeout(function() {
                try { window.frameElement && window.frameElement.parentNode.removeChild(window.frameElement); } catch(e) {}
              }, 800);
            }
            if (document.fonts && document.fonts.ready) {
              document.fonts.ready.then(function() { setTimeout(go, 50); });
            } else {
              setTimeout(go, 400);
            }
          })();
        </script>
      </body></html>`;
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open(); doc.write(html); doc.close();
  };

  if (!sku) return null;
  const copyCount = Math.max(1, Math.min(500, parseInt(copies, 10) || 1));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="sku-label-print-dialog">
        <DialogHeader><DialogTitle>Print Label Sticker</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-slate-500">
            SKU <span className="font-mono font-semibold text-slate-800">{sku.sku}</span> · {sku.name}
          </div>
          <div className="flex gap-2 flex-wrap">
            {SIZE_OPTIONS.map(s => (
              <button key={s} onClick={() => setSize(s)}
                data-testid={`sticker-size-${s}`}
                className={`px-3 py-1.5 text-xs rounded border transition ${
                  size === s ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 hover:bg-slate-50"
                }`}>{s}</button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-600">Copies</label>
            <Input type="number" min={1} max={500} value={copies}
              onChange={e => setCopies(e.target.value)}
              className="h-8 w-24" data-testid="sticker-copies" />
            {allowQuantityToggle && sku?.quantity ? (
              <>
                <button onClick={() => setCopies(1)}
                  className={`px-2.5 py-1 text-xs rounded border ${copyCount === 1 ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300"}`}
                  data-testid="sticker-qty-1">1 pc</button>
                <button onClick={() => setCopies(sku.quantity)}
                  className={`px-2.5 py-1 text-xs rounded border ${copyCount === sku.quantity ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300"}`}
                  data-testid="sticker-qty-all">All ({sku.quantity} pcs)</button>
              </>
            ) : null}
          </div>
          <div className="text-[11px] text-slate-500">
            Line 1: <b>{top || <i>— (missing)</i>}</b> · Line 2: <b>{bottom || <i>— (missing)</i>}</b>
          </div>
          <div className="text-[11px] flex items-start gap-1.5 bg-amber-50 border border-amber-200 rounded p-2 text-amber-900">
            <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>Set printer paper to <b>{size}</b>, Margins <b>None</b>, Scale <b>100% / Actual Size</b>, Headers &amp; Footers <b>Off</b>. Do <b>not</b> use "Fit to Page".</span>
          </div>
        </div>

        {/* ENLARGED SCREEN PREVIEW — for visual only. Print job uses an isolated iframe. */}
        <ScreenPreview top={top} bottom={bottom} dim={dim} topMm={fitMm.topMm} bottomMm={fitMm.bottomMm} />

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={doPrint} className="bg-slate-900" data-testid="sticker-print-btn"
            disabled={!top && !bottom}>
            <Printer className="w-4 h-4 mr-1.5" />Print {copyCount === 1 ? "1 copy" : `${copyCount} copies`} · {size}
          </Button>
        </DialogFooter>

        {/* Off-screen "ruler" copy — used only to drive the on-screen preview's auto-fit numbers */}
        <div
          key={`ruler-${sku.id}-${size}`}
          style={{
            position: "fixed", left: "-99999px", top: 0,
            visibility: "hidden",
            width: `${dim.length}mm`,
          }}
          aria-hidden="true">
          <LabelPrintable top={top} bottom={bottom} dim={dim} onFit={setFitMm} />
        </div>
      </DialogContent>
    </Dialog>
  );
}


// Popup shown after invoice import for SKUs missing sticker/family data
export function MissingStickerBulkFill({ invoiceId, onClose }) {
  const [skus, setSkus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const load = () => {
    setLoading(true);
    api.get(`/skus/missing-sticker-data${invoiceId ? `?invoice_id=${invoiceId}` : ""}`)
      .then(r => setSkus(r.data.skus || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [invoiceId]);

  const update = (id, k, v) => {
    setSkus(prev => prev.map(s => s.id === id ? { ...s, [k]: v } : s));
  };
  const save = async () => {
    setSaving(true);
    try {
      const rows = skus.map(s => ({
        id: s.id, sticker_top: s.sticker_top, sticker_bottom: s.sticker_bottom,
        sticker_size: s.sticker_size || "30mm*20mm",
        item_name_2: s.item_name_2, variant: s.variant,
      }));
      await api.post("/skus/bulk-sticker-update", { rows });
      onClose?.(true);
    } finally { setSaving(false); }
  };

  const open = skus.length > 0 || loading;
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose?.(false)}>
      <DialogContent className="max-w-6xl" data-testid="missing-sticker-dialog">
        <DialogHeader>
          <DialogTitle>Fill sticker &amp; family data ({skus.length} SKU{skus.length === 1 ? "" : "s"})</DialogTitle>
        </DialogHeader>
        <div className="text-xs text-slate-500 mb-2">
          These SKUs (from your latest invoice) are missing at least one of: 1st, 2nd, Size, Item Name 2, Variant. Fill what you can now — you can always edit later via Master SKU Export.
        </div>
        <div className="max-h-[60vh] overflow-y-auto rounded border border-slate-200">
          <table className="excel-table w-full text-xs">
            <thead className="sticky top-0"><tr>
              <th>SKU</th><th>Item Name</th>
              <th>1st (top)</th><th>2nd (bottom)</th>
              <th>Size</th>
              <th>Item Name 2</th><th>Variant</th>
            </tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-6 text-slate-400">Loading…</td></tr>
              ) : skus.map(s => (
                <tr key={s.id} data-testid={`missing-row-${s.sku}`}>
                  <td className="font-mono font-semibold">{s.sku}</td>
                  <td className="whitespace-nowrap">{s.name}</td>
                  <td><input value={s.sticker_top || ""} onChange={e => update(s.id, "sticker_top", e.target.value)}
                    data-testid={`missing-top-${s.sku}`} className="h-7 w-full border border-slate-200 rounded px-1.5" /></td>
                  <td><input value={s.sticker_bottom || ""} onChange={e => update(s.id, "sticker_bottom", e.target.value)}
                    data-testid={`missing-bottom-${s.sku}`} className="h-7 w-full border border-slate-200 rounded px-1.5" /></td>
                  <td>
                    <select value={s.sticker_size || "30mm*20mm"} onChange={e => update(s.id, "sticker_size", e.target.value)}
                      data-testid={`missing-size-${s.sku}`} className="h-7 w-full border border-slate-200 rounded px-1.5">
                      {SIZE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </td>
                  <td><input value={s.item_name_2 || ""} onChange={e => update(s.id, "item_name_2", e.target.value)}
                    data-testid={`missing-in2-${s.sku}`} className="h-7 w-full border border-slate-200 rounded px-1.5" /></td>
                  <td><input value={s.variant || ""} onChange={e => update(s.id, "variant", e.target.value)}
                    data-testid={`missing-variant-${s.sku}`} className="h-7 w-full border border-slate-200 rounded px-1.5" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose?.(false)}>Skip for now</Button>
          <Button onClick={save} data-testid="missing-save" className="bg-slate-900" disabled={saving || loading}>
            {saving ? "Saving…" : `Save ${skus.length} row${skus.length === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
